import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { getCurrentDate } from '@pesto/utils';
import { isValid } from 'date-fns';
import { parseISO } from 'date-fns/parseISO';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../../types/modal.types';

import { useAddFeeTransactionMutation } from '../../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../../components/LoanDetails/getLoanRefetchQuery';
import { createFormComponent } from '../../utils/create-form-component';

const schema = z.object({
  amountCents: z.number().min(1, 'Amount is required'),
  feeType: z.string().min(1, 'Fee type is required'),
  effectiveDate: z
    .string()
    .min(1, 'Effective date is required')
    .refine((val) => isValid(parseISO(val)), {
      message: 'Invalid date format',
    }),
  description: z.string().min(1, 'Description is required'),
});

export const AddFeeTransactionComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getLoanRefetchQuery(loanId);
  const [addFeeTransaction, { loading }] = useAddFeeTransactionMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        addFeeTransaction({
          variables: {
            loanId,
            amountCents: data.amountCents,
            feeType: data.feeType,
            effectiveDate: data.effectiveDate,
            description: data.description,
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'add-fee-transaction',
      toast: {
        success: 'Fee transaction added successfully',
        error: 'Failed to add fee transaction',
        loading: 'Adding fee transaction...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      {
        name: 'amountCents',
        label: 'Amount',
        placeholder: 'Enter amount',
        required: true,
        defaultValue: 0,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
      },
      {
        name: 'feeType',
        label: 'Fee Type',
        placeholder: 'Enter fee type',
        type: 'text',
        required: true,
      },
      {
        name: 'effectiveDate',
        label: 'Effective Date',
        type: 'date',
        required: true,
      },
      {
        name: 'description',
        label: 'Description',
        placeholder: 'Enter description',
        type: 'text',
        required: true,
      },
    ],
    schema,
    defaultValues: {
      amountCents: 0,
      feeType: '',
      effectiveDate: getCurrentDate(),
      description: '',
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
